/**
 * Order-based workflow hooks
 *
 * These hooks manage workflows that operate on ShopifyFulfillmentOrder payloads.
 * They are generated using the createWorkflowHook factory for consistency.
 *
 * Workflow hierarchy:
 * - process-order (5) spawns: split (1), fulfill-digital (2), schedule (3), update-message (4)
 *
 * @module
 */

import type { InferredFulfillmentOrder as ShopifyFulfillmentOrder } from "#/server/utils/shopify/fulfillments/schema";
import { createWorkflowHook } from "./create-workflow-hook";

// ============================================================================
// Result Types
// ============================================================================

/**
 * Result from split-fulfillment-orders workflow
 */
export interface SplitFulfillmentOrdersResult {
	physicalOrders: ShopifyFulfillmentOrder[];
	digitalOrders: ShopifyFulfillmentOrder[];
}

/**
 * Result from fulfill-digital-orders workflow
 */
export interface FulfillDigitalOrdersResult {
	fulfilledCount: number;
	fulfillmentIds: string[];
}

/**
 * Result from schedule-fulfillment-orders workflow
 */
export interface ScheduleFulfillmentOrdersResult {
	fulfillAt: string;
	fulfillBy: string;
	orderId: string;
}

/**
 * Result from update-message workflow
 */
export interface UpdateMessageResult {
	messageSet: boolean;
	orderId: string;
	message?: string;
}

/**
 * Sub-workflow operations that can be delegated to by update-snapshot
 */
export type UpdateSnapshotOperation = "split" | "fulfill-digital" | "schedule" | "update-message";

/**
 * Payload for update-snapshot workflow
 */
export interface UpdateSnapshotPayload {
	/** The order to process and merge into the snapshot */
	order: ShopifyFulfillmentOrder;
	/** Which sub-workflow operation to perform on the order */
	operation: UpdateSnapshotOperation;
	/** Optional date override, defaults to today */
	date?: string;
}

/**
 * Result from update-snapshot workflow
 */
export interface UpdateSnapshotResult {
	success: boolean;
	workflowRunId: string;
	snapshot: {
		date: string;
		version: number;
		orderId: string;
		operation: UpdateSnapshotOperation;
	};
}

/**
 * Result from process-order workflow
 */
export interface ProcessOrderResult {
	orderId: string;
	splitResult?: SplitFulfillmentOrdersResult;
	digitalFulfillmentResult?: FulfillDigitalOrdersResult;
	scheduleResult?: ScheduleFulfillmentOrdersResult;
	messageResult?: UpdateMessageResult;
}

// ============================================================================
// Workflow Hooks
// ============================================================================

/**
 * Hook for managing split-fulfillment-orders workflows.
 *
 * Splits a fulfillment order into multiple orders with 1 quantity each
 * for physical items, and separates digital items.
 *
 * @example
 * ```tsx
 * function SplitPanel({ order }: { order: ShopifyFulfillmentOrder }) {
 *   const { trigger, runs, hasActiveRuns } = useSplitFulfillmentOrders();
 *
 *   return (
 *     <button onClick={() => trigger(order)} disabled={hasActiveRuns}>
 *       Split Order
 *     </button>
 *   );
 * }
 * ```
 *
 * @remarks
 * Normally invoked by process-order workflow, but can be triggered manually
 * for damage control scenarios.
 */
export const useSplitFulfillmentOrders = createWorkflowHook<
	ShopifyFulfillmentOrder,
	SplitFulfillmentOrdersResult
>({
	type: "split-fulfillment-orders",
	channel: "workflow.split-fulfillment-orders",
});

/**
 * Hook for managing fulfill-digital-orders workflows.
 *
 * Fulfills all digital (non-shipping) items in an order immediately.
 *
 * @example
 * ```tsx
 * function DigitalFulfillPanel({ order }: { order: ShopifyFulfillmentOrder }) {
 *   const { trigger, runs } = useFulfillDigitalOrders();
 *   return <button onClick={() => trigger(order)}>Fulfill Digital Items</button>;
 * }
 * ```
 *
 * @remarks
 * Only operates on orders containing digital-only items.
 * Will error if the order contains physical items.
 */
export const useFulfillDigitalOrders = createWorkflowHook<
	ShopifyFulfillmentOrder,
	FulfillDigitalOrdersResult
>({
	type: "fulfill-digital-orders",
	channel: "workflow.fulfill-digital-orders",
});

/**
 * Hook for managing schedule-fulfillment-orders workflows.
 *
 * Sets the fulfillAt and fulfillBy dates on a fulfillment order based on
 * the _requested_date custom attribute or defaults to +1 business day.
 *
 * @example
 * ```tsx
 * function SchedulePanel({ order }: { order: ShopifyFulfillmentOrder }) {
 *   const { trigger, runs } = useScheduleFulfillmentOrders();
 *   return <button onClick={() => trigger(order)}>Schedule Order</button>;
 * }
 * ```
 *
 * @remarks
 * Requires a single line item fulfillment order.
 * Will skip if fulfillAt is already set unless override flag is provided.
 */
export const useScheduleFulfillmentOrders = createWorkflowHook<
	ShopifyFulfillmentOrder,
	ScheduleFulfillmentOrdersResult
>({
	type: "schedule-fulfillment-orders",
	channel: "workflow.schedule-fulfillment-orders",
});

/**
 * Hook for managing update-message workflows.
 *
 * Updates the message metafield on an order based on the _message
 * custom attribute from line items.
 *
 * @example
 * ```tsx
 * function MessagePanel({ order }: { order: ShopifyFulfillmentOrder }) {
 *   const { trigger, runs } = useUpdateMessage();
 *   return <button onClick={() => trigger(order)}>Update Message</button>;
 * }
 * ```
 *
 * @remarks
 * Will not overwrite existing message metafields to prevent
 * webhook-triggered resets.
 */
export const useUpdateMessage = createWorkflowHook<ShopifyFulfillmentOrder, UpdateMessageResult>({
	type: "update-message",
	channel: "workflow.update-message",
});

/**
 * Hook for managing update-snapshot workflows.
 *
 * Performs a specific operation on an order, then merges it with the latest
 * snapshot and increments the version. This is a lightweight alternative to
 * generate-snapshot when you know exactly what change needs to be made.
 *
 * @example
 * ```tsx
 * function SnapshotPanel({ order }: { order: ShopifyFulfillmentOrder }) {
 *   const { trigger, runs } = useUpdateSnapshot();
 *
 *   return (
 *     <button onClick={() => trigger({ order, operation: "schedule" })}>
 *       Update Snapshot (Schedule)
 *     </button>
 *   );
 * }
 * ```
 *
 * @remarks
 * Unlike generate-snapshot, this workflow does NOT use Shopify bulk operations.
 * It delegates to a sub-workflow (split, fulfill-digital, schedule, or update-message)
 * and then merges the result into the current snapshot.
 */
export const useUpdateSnapshot = createWorkflowHook<UpdateSnapshotPayload, UpdateSnapshotResult>({
	type: "update-snapshot",
	channel: "workflow.update-snapshot",
});

/**
 * Hook for managing process-order workflows.
 *
 * The main orchestrator workflow that processes an unmodified order through
 * the full pipeline: split → fulfill-digital → schedule → update-message.
 *
 * @example
 * ```tsx
 * function ProcessPanel({ order }: { order: ShopifyFulfillmentOrder }) {
 *   const { trigger, runs, activeRunIds } = useProcessOrder();
 *
 *   return (
 *     <div>
 *       <button onClick={() => trigger(order)}>Process Order</button>
 *       {runs.map(run => (
 *         <WorkflowRunStatus key={run.id} run={run} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 *
 * @remarks
 * This workflow invokes split (1), fulfill-digital (2), schedule (3),
 * and update-message (4) workflows as needed based on order state.
 * It intelligently skips steps that have already been completed.
 */
export const useProcessOrder = createWorkflowHook<ShopifyFulfillmentOrder, ProcessOrderResult>({
	type: "process-order",
	channel: "workflow.process-order",
});
